import {
  HttpClient, HttpHeaders, HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { apiConfiguration } from '../../../../configurations/api-configuration';
import { AccessService } from '../../../application/services/AccessService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import {
  Client,
  ClientAcquisitionSource,
  ClientListFilters,
  ClientListResult,
  SaveClientData,
} from '../../types/Client';

@Injectable()
export class ClientApiService {
  private readonly clientsUrl = `${apiConfiguration.baseApiUrl}/clients`;

  public constructor(
    private http: HttpClient,
    private accessService: AccessService,
  ) { }

  public async getClients(filters: ClientListFilters): Promise<HttpResponse<ClientListResult>> {
    let params = new HttpParams()
      .set('page', filters.page)
      .set('pageSize', filters.pageSize);

    if (filters.search.trim()) {
      params = params.set('search', filters.search.trim());
    }

    if (filters.status) {
      params = params.set('status', filters.status);
    }

    return lastValueFrom(this.http.get<HttpResponse<ClientListResult>>(this.clientsUrl, {
      headers: this.getHeaders(),
      params,
    }));
  }

  public async getClient(clientId: string): Promise<HttpResponse<Client | null>> {
    return lastValueFrom(this.http.get<HttpResponse<Client | null>>(`${this.clientsUrl}/${clientId}`, { headers: this.getHeaders() }));
  }

  public async getSources(): Promise<HttpResponse<ClientAcquisitionSource[]>> {
    return lastValueFrom(this.http.get<HttpResponse<ClientAcquisitionSource[]>>(`${this.clientsUrl}/sources`, { headers: this.getHeaders() }));
  }

  public async createClient(data: SaveClientData): Promise<HttpResponse<Client>> {
    return lastValueFrom(this.http.post<HttpResponse<Client>>(this.clientsUrl, data, { headers: this.getHeaders() }));
  }

  public async updateClient(clientId: string, data: Partial<SaveClientData>): Promise<HttpResponse<Client>> {
    return lastValueFrom(this.http.put<HttpResponse<Client>>(`${this.clientsUrl}/${clientId}`, data, { headers: this.getHeaders() }));
  }

  public async addClientNote(clientId: string, text: string): Promise<HttpResponse<Client>> {
    return lastValueFrom(this.http.post<HttpResponse<Client>>(`${this.clientsUrl}/${clientId}/notes`, { text }, { headers: this.getHeaders() }));
  }

  public async updateClientNote(clientId: string, noteId: string, text: string): Promise<HttpResponse<Client>> {
    return lastValueFrom(this.http.put<HttpResponse<Client>>(`${this.clientsUrl}/${clientId}/notes/${noteId}`, { text }, { headers: this.getHeaders() }));
  }

  public async deleteClientNote(clientId: string, noteId: string): Promise<HttpResponse<Client>> {
    return lastValueFrom(this.http.delete<HttpResponse<Client>>(`${this.clientsUrl}/${clientId}/notes/${noteId}`, { headers: this.getHeaders() }));
  }

  public async deleteClient(clientId: string): Promise<HttpResponse<void>> {
    return lastValueFrom(this.http.delete<HttpResponse<void>>(`${this.clientsUrl}/${clientId}`, { headers: this.getHeaders() }));
  }

  private getHeaders(): HttpHeaders {
    const token = this.accessService.getToken();
    return token ? new HttpHeaders({ Authorization: `Bearer ${token}` }) : new HttpHeaders();
  }
}
